import styled, { keyframes } from 'styled-components'
import { PeriodoNecesidad } from '../../../services/api'

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  animation: ${fadeIn} 0.2s ease-out;
`

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 450px;
  max-width: 90vw;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-out;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`

const PanelTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 0;
  line-height: 1;
  transition: color ${({ theme }) => theme.transitions.normal};

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
`

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const StatBox = styled.div<{ variant?: 'default' | 'danger' | 'success' }>`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ variant }) =>
        variant === 'danger' ? '#FFEBEE' :
            variant === 'success' ? '#E8F5E9' :
                '#F5F5F5'};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const StatValue = styled.div<{ variant?: 'default' | 'danger' | 'success' }>`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 700;
  color: ${({ variant }) =>
        variant === 'danger' ? '#C62828' :
            variant === 'success' ? '#2E7D32' :
                '#333'};
`

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 4px;
`

const SectionTitle = styled.h4`
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.md} 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const RoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const RoleItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const RoleName = styled.span`
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const RoleNeed = styled.span<{ urgent?: boolean }>`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ urgent }) => urgent ? '#C62828' : '#1565C0'};
`

const ProfileCard = styled.div<{ $clickable?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: all 0.2s;

  &:hover {
    ${({ theme, $clickable }) => $clickable ? `
      border-color: ${theme.colors.primary};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    ` : ''}
  }
`

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const ProfileName = styled.div`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const ProfileRole = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const ProfileLoad = styled.span<{ load: number }>`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  padding: 4px 8px;
  border-radius: 4px;
  background: ${({ load }) => load > 120 ? '#FFEBEE' : load > 100 ? '#FFF3E0' : '#FFF8E1'};
  color: ${({ load }) => load > 120 ? '#c62828' : load > 100 ? '#ef5350' : '#f57c00'};
`

const ProfileDetails = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 4px;
`

const ProjectsList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
`

const ProjectItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.textMuted};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
`

interface HiringNeedDetailPanelProps {
    isOpen: boolean
    title: string
    periodo: PeriodoNecesidad | null
    onClose: () => void
    onPersonClick?: (userId: number) => void
}

export const HiringNeedDetailPanel: React.FC<HiringNeedDetailPanelProps> = ({
    isOpen,
    title,
    periodo,
    onClose,
    onPersonClick,
}) => {
    if (!isOpen || !periodo) return null

    return (
        <>
            <Overlay onClick={onClose} />
            <Panel>
                <PanelHeader>
                    <PanelTitle>{title}</PanelTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </PanelHeader>
                <PanelContent>
                    <StatsRow>
                        <StatBox>
                            <StatValue>{periodo.necesidad_total.toFixed(1)}</StatValue>
                            <StatLabel>Necesidad</StatLabel>
                        </StatBox>
                        <StatBox variant="success">
                            <StatValue variant="success">{periodo.capacidad_disponible.toFixed(1)}</StatValue>
                            <StatLabel>Capacidad</StatLabel>
                        </StatBox>
                        <StatBox variant={periodo.deficit > 0 ? 'danger' : 'success'}>
                            <StatValue variant={periodo.deficit > 0 ? 'danger' : 'success'}>
                                {periodo.deficit.toFixed(1)}
                            </StatValue>
                            <StatLabel>Déficit</StatLabel>
                        </StatBox>
                    </StatsRow>

                    {periodo.por_rol.length > 0 && (
                        <>
                            <SectionTitle>👥 Necesidad por Rol</SectionTitle>
                            <RoleList>
                                {periodo.por_rol.map((rol, idx) => (
                                    <RoleItem key={idx}>
                                        <RoleName>{rol.rol}</RoleName>
                                        <RoleNeed urgent={rol.necesidad_personas > 1}>
                                            {rol.necesidad_personas.toFixed(1)} personas
                                        </RoleNeed>
                                    </RoleItem>
                                ))}
                            </RoleList>
                        </>
                    )}

                    {periodo.overlaps_activos.length > 0 && (
                        <>
                            <SectionTitle style={{ color: '#C62828' }}>
                                ⚠️ Personas con Sobreasignación ({periodo.overlaps_activos.length})
                            </SectionTitle>
                            {periodo.overlaps_activos.map((overlap, idx) => (
                                <ProfileCard
                                    key={idx}
                                    $clickable={!!onPersonClick}
                                    onClick={() => onPersonClick?.(overlap.user_id)}
                                >
                                    <ProfileHeader>
                                        <div>
                                            <ProfileName>{overlap.display_name}</ProfileName>
                                            <ProfileRole>@{overlap.username}</ProfileRole>
                                        </div>
                                        <ProfileLoad load={overlap.cargabilidad_total}>
                                            {overlap.cargabilidad_total.toFixed(0)}%
                                        </ProfileLoad>
                                    </ProfileHeader>
                                    <ProfileDetails>
                                        +{overlap.overlap_pct.toFixed(0)}% sobre capacidad máxima
                                    </ProfileDetails>
                                    {overlap.proyectos && overlap.proyectos.length > 0 && (
                                        <ProjectsList>
                                            {overlap.proyectos.slice(0, 5).map((proj: any, i: number) => (
                                                <ProjectItem key={i}>
                                                    <span>{proj.name || proj.opportunity_name}</span>
                                                    <span title={`${proj.pct_of_total || 0}% del total`}>
                                                        {proj.capacity || proj.load || proj.carga}%
                                                        {proj.pct_of_total != null && (
                                                            <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: '4px' }}>
                                                                ({proj.pct_of_total}%)
                                                            </span>
                                                        )}
                                                    </span>
                                                </ProjectItem>
                                            ))}
                                            {overlap.proyectos.length > 3 && (
                                                <ProjectItem style={{ color: '#999' }}>
                                                    <span>... y {overlap.proyectos.length - 3} más</span>
                                                </ProjectItem>
                                            )}
                                        </ProjectsList>
                                    )}
                                </ProfileCard>
                            ))}
                        </>
                    )}

                    {periodo.por_rol.length === 0 && periodo.overlaps_activos.length === 0 && (
                        <EmptyState>
                            <p>No hay datos detallados para este período</p>
                        </EmptyState>
                    )}
                </PanelContent>
            </Panel>
        </>
    )
}
